/**
 * Partner Detail Page
 *
 * Shows details for a single conversation partner including their
 * voice recognition status and the AI therapist's memory about them.
 * Accessible at /partners/:partnerId.
 */

import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Star, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MemoryPanel } from "@/components/partners/MemoryPanel";
import { VoiceProfileCard } from "@/components/partners/VoiceProfileCard";
import { EmptyMemoryCard } from "@/components/memory/EmptyMemoryCard";
import { useConversationPartners } from "@/hooks/useConversationPartners";
import {
  useSpeakerMemory,
  useRegenerateSpeakerMemory,
} from "@/hooks/useSpeakerMemories";
import { PARTNER_TYPE_LABELS } from "@/types/conversationPartner";
import { Layout } from "@/components/Layout";

export function PartnerDetailPage() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { data: partnersData, isLoading: partnersLoading } =
    useConversationPartners();
  const {
    data: memory,
    isLoading: memoryLoading,
    refetch: refetchMemory,
  } = useSpeakerMemory(partnerId);
  const regenerate = useRegenerateSpeakerMemory();

  const partner = partnersData?.partners.find((p) => p.id === partnerId);

  if (partnersLoading) {
    return (
      <Layout>
        <div className="container mx-auto max-w-3xl px-4 py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!partner) {
    return (
      <Layout>
        <div className="container mx-auto max-w-3xl px-4 py-8">
          <Link to="/partners">
            <Button variant="ghost" size="sm" className="mb-4 gap-2 -ml-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Partners
            </Button>
          </Link>
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Partner not found.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  const hasVoiceProfile = !!partner.voice_profile;

  return (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4 py-8">
        <Link to="/partners">
          <Button variant="ghost" size="sm" className="mb-4 gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Partners
          </Button>
        </Link>

        {/* Partner header */}
        <Card className="mb-6 p-6">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full ${
                partner.is_primary
                  ? "bg-yellow-100 dark:bg-yellow-900"
                  : "bg-muted"
              }`}
            >
              {partner.is_primary ? (
                <Star className="h-7 w-7 text-yellow-600 dark:text-yellow-400" />
              ) : (
                <User className="h-7 w-7 text-muted-foreground" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">
                  {partner.name}
                </h1>
                {partner.is_primary && (
                  <span className="rounded bg-yellow-100 dark:bg-yellow-900 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-200">
                    Primary
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>{PARTNER_TYPE_LABELS[partner.partner_type]}</span>
                {partner.pronouns && (
                  <>
                    <span>·</span>
                    <span>{partner.pronouns}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Voice recognition card */}
        <div className="mb-6">
          <VoiceProfileCard
            partnerName={partner.name}
            hasVoiceProfile={hasVoiceProfile}
          />
        </div>

        {/* Memory section */}
        {memoryLoading ? (
          <Card className="p-6">
            <div className="animate-pulse space-y-3">
              <div className="h-5 w-40 rounded bg-muted" />
              <div className="h-4 w-56 rounded bg-muted" />
              <div className="h-20 rounded bg-muted" />
            </div>
          </Card>
        ) : memory ? (
          <MemoryPanel memory={memory} onRefresh={() => refetchMemory()} />
        ) : (
          <EmptyMemoryCard
            title="Therapist Memory"
            description="No memory yet — the therapist will build a profile after analyzing your first reflection together."
            onRegenerate={async () => {
              if (!partnerId) return;
              await regenerate.mutateAsync(partnerId);
              void refetchMemory();
            }}
            regeneratePending={regenerate.isPending}
          />
        )}
      </div>
    </Layout>
  );
}
